// src/layouts/DashboardLayout.tsx
import React, { useContext, useState, useEffect, Suspense, useCallback } from "react";
import { Outlet, useLocation, Link, Navigate, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { AuthContext } from "../context/AuthContext";
import { Menu, LogOut, AlertTriangle, X, Clock, ShieldAlert } from "lucide-react";
import AICopilot from "../components/AICopilot";
import OnboardingModal from "../components/OnboardingModal";
import { LogoIcon, LogoWordmark } from "../components/brand/Logo";
import api from "../api/axios";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
type IntegrityTask = { type: string; count: number; totalAmount: number; oldestDays: number; severity: string; escalationMessage: string };

const SEV_DOT: Record<string, string> = { CRITICAL: "🔴", HIGH: "🟠", MEDIUM: "🟡", LOW: "🟢" };
const SEV_BAR: Record<string, string> = { CRITICAL: "bg-red-600", HIGH: "bg-orange-500", MEDIUM: "bg-amber-500", LOW: "bg-green-500" };
const TASK_SHORT: Record<string, string> = {
  UNIDENTIFIED_CREDIT:  "receipts",
  UNLINKED_EXPENDITURE: "expenses",
  ON_HOLD:              "on hold",
  UNPOSTED_PAYMENT:     "unposted",
};


const DashboardLayout: React.FC = () => {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [expiryBanner, setExpiryBanner] = useState<{ daysLeft: number; planName: string; frozenBalance?: number } | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [integrityTasks, setIntegrityTasks] = useState<IntegrityTask[]>([]);
  const [integrityScore, setIntegrityScore] = useState<number>(100);
  const [firstStatementId, setFirstStatementId] = useState<number | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!user?.id) return;
    const key = `pd_onboarded_${user.id}`;
    if (!localStorage.getItem(key)) setShowOnboarding(true);
  }, [user?.id]);

  const handleOnboardingClose = () => {
    if (user?.id) localStorage.setItem(`pd_onboarded_${user.id}`, '1');
    setShowOnboarding(false);
  };

  const handleIdleLogout = useCallback(() => {
    logout();
    navigate("/login?expired=true");
  }, [logout, navigate]);

  const { showWarning, secondsLeft, extendSession } = useIdleTimeout({
    timeoutMs: 15 * 60 * 1000,
    warningMs: 2 * 60 * 1000,
    onLogout: handleIdleLogout,
  });

  useEffect(() => {
    api.get("/whatsapp-subscription")
      .then((res) => {
        const sub = res.data?.subscription;
        if (!sub) return;
        const plan = res.data?.plans?.find?.((p: any) => p.code === sub.plan);
        const planName = plan?.name || sub.plan || "subscription";

        if (sub.status === "ACTIVE" && sub.endDate) {
          const daysLeft = Math.ceil((new Date(sub.endDate).getTime() - Date.now()) / 86400000);
          if (daysLeft > 0 && daysLeft <= 7) {
            setExpiryBanner({ daysLeft, planName });
          }
        }
      })
      .catch(() => {}); // silent — banner is non-critical
  }, []);

  // Fetch financial integrity data — used by sticky bar and floating notification
  useEffect(() => {
    api.get("/bank-statements/discrepancies")
      .then(r => {
        setIntegrityTasks(r.data.tasks ?? []);
        setIntegrityScore(r.data.integrityScore ?? 100);
        setFirstStatementId(r.data.statements?.[0]?.id ?? null);
      })
      .catch(() => {});
  }, [location.pathname]); // refresh when navigating to pick up resolved tasks

  // Prefetch the most-visited route chunks during idle time so navigating to
  // them feels instant. Module specifiers match the lazy() imports in App.tsx,
  // so these share the same chunks (webpack dedupes — no double download).
  useEffect(() => {
    const w = window as any;
    const schedule: (cb: () => void) => any = w.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1500));
    const cancel: (h: any) => void = w.cancelIdleCallback || clearTimeout;
    const handle = schedule(() => {
      import("../pages/Payments");
      import("../pages/ResidentsList");
      import("../pages/OwnerList");
      import("../pages/LedgerReport");
    });
    return () => cancel(handle);
  }, []);

  if (user?.role === "RESIDENT") {
    return <Navigate to="/resident" replace />;
  }

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    const mapping: Record<string, string> = {
      'dashboard': 'Overview',
      'collections': 'Financials → Collections',
      'payments': 'Financials → Receipts',
      'receipts': 'Financials → Receipts',
      'ledger': 'Reports → Ledger',
      'flats': 'Infrastructure → Units',
      'blocks': 'Infrastructure → Blocks',
      'residents': 'Registry → Residents',
      'owners': 'Registry → Owners',
      'owners/new': 'Registry → Owners → New',
      'suppliers': 'Utilities → Suppliers',
      'water-purchases': 'Utilities → Water Purchases',
      'water-consumption': 'Utilities → Water Consumption',
      'water-billing': 'Utilities → Water Billing',
      'electricity-bills': 'Utilities → Electricity Bills',
      'staff': 'Registry → Staff',
      'billing-summary': 'Billing → Monthly Statements',
      'smart-import': 'Property Setup → Smart Import Studio',
      'summary': 'Property Setup → Smart Import Studio → Import Summary',
      'whatsapp-settings': 'Settings → WhatsApp Config',
      'whatsapp-alerts': 'Settings → Subscriptions',
      'notification-preferences': 'Settings → Notification Preferences',
      'payment-activation': 'Settings → NoCash Setup',
      'email-settings': 'Settings → Email Config',
      'email-config': 'Settings → Email Config',
      'vacancies': 'Growth → Vacancies',
      'leads': 'Growth → Lead Management',
      'communication-logs': 'System → Communication Logs',
      'financial-health': 'Analytics → Financial Health',
      'budget': 'Reports → Budget',
      'roles-permissions': 'Settings → Roles & Permissions',
      'user-management': 'Settings → User Management',
      'treasury': 'Accounts & Funds → Financial Position',
      'accounts': 'Accounts & Funds → Accounts',
      'bank-accounts': 'Accounts & Funds → Bank Accounts',
      'cash-in-hand': 'Accounts & Funds → Cash Account',
      'funds': 'Accounts & Funds → Funds',
      'cashbook': 'Accounts & Funds → Cash Flow',
      'account-statements': 'Accounts & Funds → Account Passbook',
      'verification-report': 'Accounts & Funds → Verification Report',
      'bank-reconciliation': 'Accounts & Funds → Bank Reconciliation',
      'balance-sheet': 'Accounts & Funds → Balance Sheet',
    };

    const categoryPaths: Record<string, string> = {
      'Financials': '/dashboard/collections',
      'Reports': '/dashboard/ledger',
      'Owners': '/dashboard/owners',
      'Infrastructure': '/dashboard/blocks',
      'Registry': '/dashboard/residents',
      'Utilities': '/dashboard/suppliers',
      'Billing': '/dashboard/billing-summary',
      'System': '/dashboard/smart-import',
      'Settings': '/dashboard/billing-rules',
      'Growth': '/dashboard/vacancies',
      'Analytics': '/dashboard/financial-health'
    };

    const key = segments[segments.length - 1];
    const isWaterEntryReview = /\/water-purchases\/entries\/\d+$/.test(path);
    const fullLabel = (isWaterEntryReview ? 'Utilities → Water Purchases → Pending Approval' : mapping[key]) || segments
      .filter(s => s !== 'dashboard')
      .map(s => s.replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
      .join(' → ') || 'Overview';

    return fullLabel.split(' → ').map((label, index, arr) => {
      let segmentPath = '#';
      if (index === arr.length - 1) {
        segmentPath = location.pathname;
      } else if (categoryPaths[label]) {
        segmentPath = categoryPaths[label];
      } else {
        const lowerLabel = label.toLowerCase().replace(/\s+/g, '-');
        const match = segments.find(s => s.includes(lowerLabel) || lowerLabel.includes(s));
        if (match) {
          segmentPath = `/dashboard/${match}`;
        }
      }

      return {
        label,
        path: segmentPath,
        isLast: index === arr.length - 1
      };
    });
  };

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => setCollapsed((s) => !s);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 sticky top-0 h-screen self-start">
        <Sidebar onLogout={handleLogout} collapsed={collapsed} onToggle={toggleSidebar} />
      </div>

      {/* Mobile drawer */}
      <div className="md:hidden">
        {/* top bar (Premium Glass) */}
        <div className="flex items-center justify-between p-3 bg-[#fcfcfd]/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-[14px] hover:bg-slate-100 text-slate-600"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <LogoIcon light={false} className="h-7 w-auto" />
              <LogoWordmark light={false} className="h-5 w-auto" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-sm text-slate-500">Hi{user?.email ? `, ${user.email.split('@')[0]}` : ""}</div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-[14px] hover:bg-red-50 text-slate-500 hover:text-red-600 transition-all active:scale-95"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>

        {/* drawer panel */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 flex">
            <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
            <div className="relative w-64 bg-white border-r border-gray-200">
              <Sidebar onLogout={() => { setMobileOpen(false); handleLogout(); }} />
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="hidden md:flex flex-col bg-[#fcfcfd]/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-30">
          {/* Finance Action Bar — compact strip, only shown when tasks exist and not on reconciliation page */}
          {integrityTasks.length > 0 && !location.pathname.includes("bank-reconciliation") && (
            <div className="flex items-center gap-3 px-6 py-1.5 bg-red-600 border-b border-red-700">
              <ShieldAlert size={13} className="text-red-200 shrink-0" />
              <span className="text-[10px] font-bold text-red-100 uppercase tracking-wider shrink-0">
                Finance Actions ({integrityTasks.length})
              </span>
              <div className="flex items-center gap-3 flex-1 overflow-hidden">
                {integrityTasks.map(t => (
                  <span key={t.type} className="text-[10px] text-red-100 shrink-0 whitespace-nowrap">
                    {SEV_DOT[t.severity]} {t.count} {TASK_SHORT[t.type] ?? t.type.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
              <button
                onClick={() => navigate(
                  firstStatementId
                    ? `/dashboard/treasury/bank-reconciliation/${firstStatementId}?autoReview=1`
                    : "/dashboard/treasury/bank-reconciliation"
                )}
                className="shrink-0 px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white text-[10px] font-bold rounded-lg transition-colors"
              >
                Resolve Now →
              </button>
            </div>
          )}

          {/* Breadcrumb / user row */}
          <div className="flex items-center justify-between px-6 py-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {getBreadcrumbs().map((crumb, i) => (
                  <React.Fragment key={i}>
                    <Link
                      to={crumb.path}
                      className={`text-[10px] font-bold uppercase tracking-[0.2em] transition-colors hover:text-primary ${crumb.isLast ? 'text-primary' : 'text-slate-300'}`}
                    >
                      {crumb.label}
                    </Link>
                    {!crumb.isLast && <span className="text-slate-300 text-[10px]">/</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end">
                <div className="text-xs font-semibold text-primary uppercase tracking-widest mb-0.5">
                  {user?.tenantName || "Society Manager"}
                </div>
                <div className="text-[10px] text-slate-400 font-bold">
                  {user?.email} ({user?.role})
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Subscription expiry banner — shown 7 days before expiry, hidden on the subscriptions page */}
        {!bannerDismissed && expiryBanner && !location.pathname.includes("whatsapp-alerts") && (
          <div className="mx-4 md:mx-6 mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-amber-800 flex-1">
              Your <strong>{expiryBanner.planName}</strong> subscription expires in{" "}
              <strong>{expiryBanner.daysLeft} day{expiryBanner.daysLeft !== 1 ? "s" : ""}</strong>.{" "}
              <Link to="/dashboard/whatsapp-alerts" className="underline font-semibold hover:text-amber-900">
                Renew now →
              </Link>
            </span>
            <button
              onClick={() => setBannerDismissed(true)}
              className="text-amber-400 hover:text-amber-600 flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <main className="px-4 md:px-6 pt-2 pb-6">
          <Suspense fallback={<div className="flex items-center justify-center py-24"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {showOnboarding && <OnboardingModal onClose={handleOnboardingClose} />}

      <AICopilot />

      {/* ── Idle Session Warning Modal ── */}
      {showWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 flex flex-col items-center gap-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
              <Clock className="w-7 h-7 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Still there?</h3>
              <p className="text-sm text-gray-500 mt-1">
                You'll be signed out due to inactivity in{" "}
                <span className="font-bold text-amber-600">
                  {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")}
                </span>
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={handleIdleLogout}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Sign out
              </button>
              <button
                onClick={extendSession}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
